import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import {
  useInventoryProcess,
  useInventoryAssetsPaginated,
  useInventoryStats,
  useInventoryUnexpectedEpcs,
  InventoryAsset,
} from '@/api/hooks/useInventory';
import {
  ArrowLeft,
  CheckCircle,
  MapPin,
  AlertTriangle,
  Clock,
  Download,
  ClipboardList,
  Eye,
  HelpCircle,
  ScanLine,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { IncidentDetailDialog } from '@/pages/incidents/components/IncidentDetailDialog';
import InventoryReconcileCard from './InventoryReconcileCard';
import { isTocaTenant } from '@/config/tenant';

// Status colors
const statusColors: Record<number, { bg: string; text: string }> = {
  0: { bg: 'bg-gray-100', text: 'text-gray-700' }, // Not scanned / Pending
  1: { bg: 'bg-green-100', text: 'text-green-700' }, // OK - Correct location
  2: { bg: 'bg-yellow-100', text: 'text-yellow-700' }, // Wrong location
  3: { bg: 'bg-red-100', text: 'text-red-700' }, // With incident
  4: { bg: 'bg-gray-100', text: 'text-gray-500' }, // Pending
};

// Status icons
const StatusIcon = ({ status }: { status: number }) => {
  switch (status) {
    case 1:
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    case 2:
      return <MapPin className="h-5 w-5 text-yellow-600" />;
    case 3:
      return <AlertTriangle className="h-5 w-5 text-red-600" />;
    default:
      return <Clock className="h-5 w-5 text-gray-500" />;
  }
};

export default function InventoryDetailPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { id } = useParams<{ id: string }>();
  const inventoryId = parseInt(id || '0');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Status filter state
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  // Incident detail dialog state
  const [incidentAsset, setIncidentAsset] = useState<{ assetId: string; assetName: string } | null>(null);

  // Fetch inventory data
  const { data: inventory, isLoading: inventoryLoading, error: inventoryError } = useInventoryProcess(inventoryId);
  const { data: assetsData, isLoading: assetsLoading, isFetching: assetsFetching } = useInventoryAssetsPaginated(
    inventoryId,
    {
      page,
      pageSize,
      statusFilter: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    }
  );
  const { data: stats, isLoading: statsLoading } = useInventoryStats(inventoryId);
  // Sólo TOCA: el esperado por estatus de planta —y con él la noción de "leído
  // pero no esperado"— es de la industria de tarimas. El bundle del portal es el
  // mismo para SGS, así que allá la consulta ni siquiera se dispara.
  const showUnexpected = isTocaTenant();
  const [unexpectedPage, setUnexpectedPage] = useState(1);
  const { data: unexpected, isLoading: unexpectedLoading } =
    useInventoryUnexpectedEpcs(inventoryId, unexpectedPage, showUnexpected);

  // Toggle status filter - reset to page 1 when filter changes
  const toggleStatusFilter = (status: number | number[]) => {
    const statuses = Array.isArray(status) ? status : [status];
    setSelectedStatuses(prev => {
      const hasAny = statuses.some(s => prev.includes(s));
      if (hasAny) {
        return prev.filter(s => !statuses.includes(s));
      }
      return [...prev, ...statuses];
    });
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedStatuses([]);
    setPage(1);
  };

  // Get status label
  const getStatusLabel = (statusCode: number): string => {
    switch (statusCode) {
      case 1: return t('detail.statusTypes.ok');
      case 2: return t('detail.statusTypes.wrongLocation');
      case 3: return t('detail.statusTypes.incident');
      case 4: return t('detail.statusTypes.pending');
      default: return t('detail.statusTypes.notScanned');
    }
  };

  // Table columns
  const columns = useMemo<ColumnDef<InventoryAsset>[]>(
    () => [
      {
        accessorKey: 'assetId',
        header: t('detail.assetTable.assetId'),
        cell: ({ row }) => (
          <Link
            to={`/assets/${row.original.assetId}`}
            className="font-medium text-primary hover:underline"
          >
            {row.original.assetId}
          </Link>
        ),
      },
      {
        accessorKey: 'epcCode',
        header: t('detail.assetTable.epc'),
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.epcCode || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'assetName',
        header: t('detail.assetTable.description'),
        cell: ({ row }) => row.original.assetName || '-',
      },
      {
        accessorKey: 'hasIncidents',
        header: t('detail.assetTable.incidents'),
        cell: ({ row }) => (
          row.original.hasIncidents ? (
            <Button
              variant="destructive"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={() =>
                setIncidentAsset({
                  assetId: row.original.assetId,
                  assetName: row.original.assetName || row.original.assetId,
                })
              }
            >
              <Eye className="h-3 w-3" />
              Ver
            </Button>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        ),
      },
      {
        accessorKey: 'statusCode',
        header: t('detail.assetTable.status'),
        cell: ({ row }) => {
          const statusCode = row.original.statusCode;
          const colors = statusColors[statusCode] || statusColors[0];
          return (
            <div className="flex items-center gap-2">
              <StatusIcon status={statusCode} />
              <span className={cn('text-xs', colors.text)}>
                {getStatusLabel(statusCode)}
              </span>
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Loading state
  if (inventoryLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state
  if (inventoryError || !inventory) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold mb-2">{t('detail.error')}</h2>
        <Link to="/inventory">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/inventory">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t('detail.title')} #{inventory.id}
            </h1>
            <p className="text-muted-foreground">
              {inventory.locationName || t('allLocations')} | {t('started')}:{' '}
              {format(new Date(inventory.startDate), "d 'de' MMM yyyy", { locale: es })}
              {inventory.endDate && (
                <> | {t('ended')}: {format(new Date(inventory.endDate), "d 'de' MMM yyyy", { locale: es })}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {t('detail.exportButton')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            selectedStatuses.includes(1) && 'ring-2 ring-green-500'
          )}
          onClick={() => toggleStatusFilter(1)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('detail.stats.correctLocation')}
            </CardTitle>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.ok || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            selectedStatuses.includes(2) && 'ring-2 ring-yellow-500'
          )}
          onClick={() => toggleStatusFilter(2)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('detail.stats.wrongLocation')}
            </CardTitle>
            <MapPin className="h-5 w-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.wrongLocation || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            selectedStatuses.includes(3) && 'ring-2 ring-red-500'
          )}
          onClick={() => toggleStatusFilter(3)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('detail.stats.withIncidents')}
            </CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.notFound || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:shadow-md',
            (selectedStatuses.includes(0) || selectedStatuses.includes(4)) && 'ring-2 ring-gray-500'
          )}
          onClick={() => toggleStatusFilter([0, 4])}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('detail.stats.pending')}
            </CardTitle>
            <Clock className="h-5 w-5 text-gray-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.pending || 0}</div>
            )}
          </CardContent>
        </Card>

        {/* No filtra la tabla de activos: estos EPC no son filas de
            InventoryProcessAsset, viven en su propia lista más abajo. */}
        {showUnexpected && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('detail.stats.unregistered')}
            </CardTitle>
            <HelpCircle className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            {unexpectedLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{unexpected?.unregisteredCount || 0}</div>
            )}
            {!unexpectedLoading && (unexpected?.registeredCount || 0) > 0 && (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('detail.stats.unexpectedRegistered', {
                  count: unexpected?.registeredCount || 0,
                })}
              </p>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Incident Detail Dialog */}
      {incidentAsset && (
        <IncidentDetailDialog
          open={!!incidentAsset}
          onOpenChange={(open) => !open && setIncidentAsset(null)}
          mode="asset"
          assetId={incidentAsset.assetId}
          assetName={incidentAsset.assetName}
        />
      )}

      {/* EPCs leídos que el inventario no esperaba. Sólo aparece cuando hay
          alguno: en un inventario limpio no tiene nada que decir. */}
      {showUnexpected && (unexpected?.totalRecords || 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              {t('detail.unexpected.title')}
            </CardTitle>
            <CardDescription>{t('detail.unexpected.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">{t('detail.unexpected.epc')}</th>
                    <th className="py-2 pr-4 font-medium">{t('detail.unexpected.kind')}</th>
                    <th className="py-2 pr-4 font-medium">{t('detail.unexpected.assetId')}</th>
                    <th className="py-2 pr-4 font-medium">{t('detail.unexpected.assetStatus')}</th>
                    <th className="py-2 font-medium">{t('detail.unexpected.location')}</th>
                  </tr>
                </thead>
                <tbody>
                  {unexpected?.items.map((row) => (
                    <tr key={row.epc} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono">{row.epc}</td>
                      <td className="py-2 pr-4">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-medium',
                            row.kind === 'unregistered'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          )}
                        >
                          {row.kind === 'unregistered'
                            ? t('detail.unexpected.kindUnregistered')
                            : t('detail.unexpected.kindRegistered')}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{row.assetId || '—'}</td>
                      <td className="py-2 pr-4">{row.assetStatusName || '—'}</td>
                      <td className="py-2">{row.locationName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(unexpected?.totalPages || 0) > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  {t('detail.unexpected.pageOf', {
                    page: unexpected?.page || 1,
                    total: unexpected?.totalPages || 1,
                  })}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(unexpected?.page || 1) <= 1}
                    onClick={() => setUnexpectedPage((p) => Math.max(1, p - 1))}
                  >
                    {t('detail.unexpected.prev')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(unexpected?.page || 1) >= (unexpected?.totalPages || 1)}
                    onClick={() => setUnexpectedPage((p) => p + 1)}
                  >
                    {t('detail.unexpected.next')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 15 — Reconciliar diferencias. Renders only after Finalizar
          when there are still unresolved diffs OR an adjustment history. */}
      <InventoryReconcileCard
        processId={inventoryId}
        isClosed={inventory.status === 'Closed'}
        locationName={inventory.locationName}
      />

      {/* Assets Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                {t('detail.assetList')}
              </CardTitle>
              <CardDescription>
                {inventory.scannedAssets} / {inventory.totalAssets} {t('detail.scannedAssets').toLowerCase()}
                {selectedStatuses.length > 0 && assetsData && (
                  <span className="ml-2">
                    ({assetsData.totalCount} {t('common:labels.filtered')})
                  </span>
                )}
              </CardDescription>
            </div>
            {selectedStatuses.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {t('filters.clearFilters')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {assetsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !assetsData || assetsData.items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('detail.noAssets')}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={assetsData.items}
              isLoading={assetsFetching && !assetsLoading}
              pageCount={assetsData.totalPages}
              pageIndex={page - 1}
              pageSize={pageSize}
              totalItems={assetsData.totalCount}
              onPageChange={(pageIndex) => setPage(pageIndex + 1)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
